// filepath: apps/web/src/features/cabinet/CabinetLandingPage.tsx
//
// FR-UI-CABINET-1 AC-3 / AC-8 — the "My Cabinet" default landing.
//
// Composes ONLY EXISTING tenant-scoped data sources (no new backend
// endpoint is introduced, called, or stubbed under this FR):
//   - `useTasks({ view: "mine" })` — tasks due-soon count
//     (FR-UI-TASK-1 / FR-HT-1..3).
//   - `useMetricsSummary()` — open + failed process counts
//     (FR-UI-DASH-1 / FR-OBS-3).
//
// AC-8 (ARCH_WEB §5 — 5 mandatory error states): each source
// degrades INDEPENDENTLY. A failed metrics fetch must not blank the
// tasks tile, and vice-versa. Each tile handles its own:
//   - loading          → skeleton matching layout
//   - fetch-failure    → retry + non-sensitive message
//   - permission-denied (403) → "no access", no detail
//   - stale-version    (404/410) → definition-version-gone message
//   - rate-limit       (429) → Retry-After countdown
//
// AC-5 descope: the "recent records" strip (one card per readable
// entity type) is part of the AC-5 descope — it needs
// `readableEntityTypes`, which is `[]` in v1. A documented seam
// renders a placeholder until `/api/me` lands. See
// `RecentRecordsStrip` below.
//
// INV-1: every composed hook already includes `tenantSlug` in its
// cache key (`useTasks`, `useMetricsSummary`) — no new tenant-data
// path is introduced. INV-2: the SPA renders exactly what the server
// returns. INV-9: all chrome via `t(key, fallback)` text nodes.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useTasks } from "@/features/tasks/useTasks";
import { useMetricsSummary } from "@/features/observability/queries";
import type { HumanTask } from "@/features/tasks/types";
import { ApiError } from "@/definitions/types";
import { NEUTRAL, SPACE, RADIUS_MD } from "./styles";
import { Button } from "@/design-system/Button";

/** "Due soon" window — tasks due within 7 days OR overdue. */
const DUE_SOON_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Count tasks that are due soon (overdue or due within 7 days).
 * Pure + deterministic so TestDesigner can assert against fixed
 * `due_at` fixtures. Tasks with a null / unparseable `due_at` are
 * excluded (no due date ⇒ not "due soon").
 */
export function countTasksDueSoon(
  tasks: readonly HumanTask[],
  now: number = Date.now(),
): number {
  return tasks.filter((task) => {
    if (!task.due_at) return false;
    const dueMs = Date.parse(task.due_at);
    if (!Number.isFinite(dueMs)) return false;
    return dueMs <= now + DUE_SOON_WINDOW_MS;
  }).length;
}

/** The 5-state classification (mirrors FR-UI-DASH-1's pattern). */
type TileStatus =
  | "loading"
  | "fetch-failure"
  | "permission-denied"
  | "stale-version"
  | "rate-limit"
  | "success";

function classifyTileError(err: ApiError): TileStatus {
  if (err.status === 403) return "permission-denied";
  if (err.status === 404 || err.status === 410) return "stale-version";
  if (err.status === 429 || err.code === "rate_limited") return "rate-limit";
  return "fetch-failure";
}

// ─── CabinetLandingPage ───────────────────────────────────────────────

export function CabinetLandingPage(): React.ReactElement {
  const { t } = useTranslation();
  // Each source degrades independently (AC-8). Both metrics tiles
  // share the one metrics query (TanStack dedupes the call).
  const tasksQ = useTasks({ view: "mine" });
  const metricsQ = useMetricsSummary();

  return (
    <div data-testid="cabinet-landing" style={{ padding: SPACE[6] }}>
      <header style={{ marginBottom: SPACE[6] }}>
        <h2
          data-testid="cabinet-landing-title"
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: NEUTRAL.textPrimary,
            margin: 0,
          }}
        >
          {t("cabinet.landing.title", "My Cabinet")}
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: NEUTRAL.textSecondary,
            margin: "4px 0 0 0",
          }}
        >
          {t("cabinet.landing.subtitle", "Your work at a glance.")}
        </p>
      </header>

      <section
        aria-label={t("cabinet.landing.tiles_label", "Summary")}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: SPACE[3],
          marginBottom: SPACE[6],
        }}
      >
        <SummaryTile
          testId="cabinet-tile-tasks-due"
          label={t("cabinet.landing.tasks_due_soon", "Tasks due soon")}
          query={{
            isLoading: tasksQ.isLoading,
            error: tasksQ.error,
            refetch: () => {
              void tasksQ.refetch();
            },
          }}
          renderValue={() => {
            const tasks = tasksQ.tasks ?? [];
            return String(countTasksDueSoon(tasks));
          }}
          danger={false}
        />
        <SummaryTile
          testId="cabinet-tile-open-processes"
          label={t("cabinet.landing.open_processes", "Open processes")}
          query={{
            isLoading: metricsQ.isLoading,
            error: metricsQ.error,
            refetch: () => {
              void metricsQ.refetch();
            },
          }}
          renderValue={() => String(metricsQ.data?.instance_count ?? 0)}
          danger={false}
        />
        <SummaryTile
          testId="cabinet-tile-failed-processes"
          label={t("cabinet.landing.failed_processes", "Failed processes")}
          query={{
            isLoading: metricsQ.isLoading,
            error: metricsQ.error,
            refetch: () => {
              void metricsQ.refetch();
            },
          }}
          renderValue={() => String(metricsQ.data?.error_count ?? 0)}
          danger
        />
      </section>

      <RecentRecordsStrip />
    </div>
  );
}

// ─── SummaryTile (independent 5-state degradation) ────────────────────

interface SummaryTileProps {
  testId: string;
  label: string;
  query: {
    isLoading: boolean;
    error: ApiError | null;
    refetch: () => void;
  };
  renderValue: () => string;
  /** Render the numeric value in the danger colour (failed processes). */
  danger: boolean;
}

function SummaryTile({
  testId,
  label,
  query,
  renderValue,
  danger,
}: SummaryTileProps): React.ReactElement {
  // `label` arrives pre-translated from the caller; this tile does
  // not call t() directly (error chrome is handled by TileErrorState).

  // ── loading ──
  if (query.isLoading) {
    return (
      <div
        data-testid={`${testId}--loading`}
        aria-busy="true"
        aria-live="polite"
        style={tileShellStyle}
      >
        <div style={{ ...skeletonBar, width: "60%", height: "12px" }} />
        <div style={{ ...skeletonBar, width: "40%", height: "28px" }} />
      </div>
    );
  }

  // ── error states (fetch-failure / permission-denied / stale / rate-limit) ──
  if (query.error) {
    const status = classifyTileError(query.error);
    return (
      <TileErrorState
        testId={testId}
        status={status}
        error={query.error}
        onRetry={query.refetch}
      />
    );
  }

  // ── success ──
  const value = renderValue();
  return (
    <div data-testid={testId} style={tileShellStyle}>
      <div style={{ fontSize: "12px", color: NEUTRAL.textSecondary }}>
        {label}
      </div>
      <div
        style={{
          fontSize: "28px",
          fontWeight: 700,
          // ISS-436 (ui-conformance, sweep finding 12 — the design-adherence-ignore above keeps the bare issue number in the comment from matching the text-scan hex pattern; it is not a colour value): use the
          // design-system danger token via the alias split rather
          // than the raw hex `SEMANTIC.danger` (a "literal red"). The
          // token resolves through the design-system stylesheet
          // (`--color-danger`) so future palette tweaks propagate
          // without touching chrome.
          color: danger ? "var(--color-danger)" : NEUTRAL.textPrimary,
          marginTop: "4px",
        }}
      >
        {value}
      </div>
    </div>
  );
}

const tileShellStyle: React.CSSProperties = {
  padding: SPACE[4],
  background: NEUTRAL.surfaceCard,
  border: `1px solid ${NEUTRAL.borderDefault}`,
  borderRadius: RADIUS_MD,
  minHeight: "84px",
};

const skeletonBar: React.CSSProperties = {
  background: NEUTRAL.borderDefault,
  borderRadius: RADIUS_MD,
  marginTop: "8px",
};

// ─── TileErrorState ───────────────────────────────────────────────────

interface TileErrorStateProps {
  testId: string;
  status: TileStatus;
  error: ApiError;
  onRetry: () => void;
}

function TileErrorState({
  testId,
  status,
  error,
  onRetry,
}: TileErrorStateProps): React.ReactElement {
  const { t } = useTranslation();

  const titleKey = ((): string => {
    switch (status) {
      case "permission-denied":
        return "cabinet.errors.permission_denied";
      case "rate-limit":
        return "cabinet.errors.rate_limit";
      case "stale-version":
        return "cabinet.errors.stale_version";
      case "fetch-failure":
      default:
        return "cabinet.errors.fetch_failure";
    }
  })();

  const titleFallback = ((): string => {
    switch (status) {
      case "permission-denied":
        return "You do not have access to this.";
      case "rate-limit":
        return "Too many requests. Please wait.";
      case "stale-version":
        return "This version is no longer available.";
      case "fetch-failure":
      default:
        return "Failed to load.";
    }
  })();

  return (
    <div
      data-testid={`${testId}--error`}
      data-error-kind={status}
      role="alert"
      style={tileShellStyle}
    >
      <div style={{ fontSize: "12px", color: NEUTRAL.textSecondary }}>
        {t("cabinet.errors.tile_label", "Summary")}
      </div>
      <p
        style={{
          fontSize: "14px",
          color: "var(--color-danger)",
          margin: "4px 0 0 0",
        }}
      >
        {t(titleKey, titleFallback)}
      </p>
      {status === "rate-limit" && error.retryAfterSeconds !== null && (
        <p
          style={{
            fontSize: "12px",
            color: NEUTRAL.textTertiary,
            margin: "4px 0 0 0",
          }}
        >
          {t("cabinet.errors.retry_after", "Retry available in {{seconds}}s.", {
            seconds: error.retryAfterSeconds,
          })}
        </p>
      )}
      {status !== "permission-denied" && status !== "stale-version" && (
        <button
          type="button"
          data-testid={`${testId}--retry`}
          onClick={onRetry}
          style={{
            marginTop: "8px",
            padding: `${SPACE[2]} ${SPACE[3]}`,
            fontSize: "12px",
            border: `1px solid ${NEUTRAL.borderDefault}`,
            borderRadius: RADIUS_MD,
            background: NEUTRAL.surfaceCard,
            color: NEUTRAL.textPrimary,
            cursor: "pointer",
          }}
        >
          {t("common.retry", "Retry")}
        </button>
      )}
    </div>
  );
}

// ─── RecentRecordsStrip (AC-5 descope seam) ───────────────────────────

/**
 * The recent-records strip — one card per readable entity type.
 *
 * AC-5 DESCOPE: this requires `readableEntityTypes` from the nav
 * manifest, which is `[]` in v1 (per-entity-type READ perms are not
 * derivable from the JWT and `/api/me` does not exist yet — see
 * `useNavManifest.ts` / `types.ts`). Until the follow-up
 * `FR-UI-CORE-*` `/api/me` FR ships, this renders a typed placeholder
 * with a documented seam.
 *
 * When `/api/me` lands: read `useNavManifest().readableEntityTypes`,
 * render one card per type deep-linking `/lists/:entityType`
 * (FR-UI-LIST-1), and fetch the latest record per type via the
 * existing entity-records query. This component is the single place
 * that change touches.
 */
function RecentRecordsStrip(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <section
      data-testid="cabinet-recent-records"
      style={{ marginTop: SPACE[4] }}
    >
      <h3
        style={{
          fontSize: "14px",
          fontWeight: 600,
          color: NEUTRAL.textPrimary,
          margin: "0 0 8px 0",
        }}
      >
        {t("cabinet.landing.recent_records", "Recent records")}
      </h3>
      <div
        data-testid="cabinet-recent-records-placeholder"
        style={{
          padding: SPACE[4],
          border: `1px dashed ${NEUTRAL.borderDefault}`,
          borderRadius: RADIUS_MD,
          color: NEUTRAL.textTertiary,
          fontSize: "13px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: SPACE[3],
        }}
      >
        {/* ISS-436 (ui-conformance, sweep finding 10 — the design-adherence-ignore above keeps the bare issue number from matching the text-scan hex pattern): the empty state
            used to be text-only. The new design adds (a) a small
            icon to signal the slot is intentional, and (b) a
            design-system Button action that links to the directory
            admin where per-entity permissions are configured. The
            "no permissions configured" state is now a discoverable
            action, not a dead-end paragraph. */}
        <span
          aria-hidden="true"
          data-testid="cabinet-recent-records-empty-icon"
          // design-adherence-ignore: 32x32 icon wrapper is a pre-existing
          // design-system primitive pattern (e.g. the Topbar avatar
          // uses the same size). No design-system token exists for
          // the icon size; the value is intentional and the pattern
          // is consistent with the rest of the chrome.
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: NEUTRAL.surfacePage,
            color: NEUTRAL.textTertiary,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="13" x2="15" y2="13" />
            <line x1="9" y1="17" x2="13" y2="17" />
          </svg>
        </span>
        <span>
          {t(
            "cabinet.landing.recent_records.placeholder",
            "Recent records appear here once per-entity-type permissions are configured.",
          )}
        </span>
        <Button
          variant="secondary"
          onClick={() => {
            window.location.assign("/directory/admin");
          }}
        >
          {t(
            "cabinet.landing.recent_records.configureAction",
            "Configure permissions",
          )}
        </Button>
      </div>
    </section>
  );
}
